import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./button";
import { cn } from "../lib/cn";
import { Trans } from "@lingui/react/macro";

export type ConfirmDialogProps = {
  open: boolean;
  /** Names the object: "Disable Samar Hassan's access?" */
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  className?: string;
};

/**
 * Native modal <dialog>: sharp, hairline border, and the only shadow in the system.
 * For confirmations only — anything that fits inline stays inline.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  pending = false,
  error,
  onConfirm,
  onCancel,
  className,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() focuses the first control (the destructive one); a confirmation must start on Cancel.
      cancelRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onCancel}
      className={cn(
        "m-auto w-[440px] max-w-[calc(100vw-32px)] border border-line-strong bg-surface p-6 text-ink shadow-overlay",
        "backdrop:bg-ink/40 open:flex open:flex-col open:gap-4",
        className,
      )}
    >
      <h2 className="font-display text-xl text-ink">{title}</h2>
      {description && <div className="text-sm text-ink-2">{description}</div>}
      {error && <p className="bg-danger-soft px-3 py-2 text-xs text-danger">{error}</p>}
      <div className="mt-2 flex items-center gap-2">
        <Button variant={tone} onClick={onConfirm} disabled={pending}>
          {pending ? <Trans>Working…</Trans> : confirmLabel}
        </Button>
        <Button ref={cancelRef} variant="ghost" onClick={onCancel} disabled={pending}>
          {cancelLabel ?? <Trans>Cancel</Trans>}
        </Button>
      </div>
    </dialog>
  );
}
